// src/components/ApplicantDetailsModal.jsx
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Descriptions,
    Modal,
    Row,
    Space,
    Tag,
    Typography,
    notification,
    Select,
} from "antd";
import React, { useState, useEffect } from "react";
import { STATUS_COLORS, formatDate } from "./utils";

const { Title } = Typography;

const ApplicantDetailsModal = ({ visible, applicant, onClose, onStatusUpdate, fetchApplicants }) => {
    const [updateLoading, setUpdateLoading] = useState(false);
    const [editedStrand, setEditedStrand] = useState(applicant?.academicStrand || "");
    const [targetStatus, setTargetStatus] = useState(null);

    const ACADEMIC_STRAND_OPTIONS = [
        { value: "STEM", label: "STEM" },
        { value: "HUMSS", label: "HUMSS" },
        { value: "ABM", label: "ABM" },
        { value: "TVL", label: "TVL" },
        { value: "GAS", label: "GAS" },
    ];

    useEffect(() => {
        if (applicant) {
            setEditedStrand(applicant.academicStrand);
        }
    }, [applicant]);

    if (!applicant) return null;

    const renderStatusTag = (status) => {
        const color = STATUS_COLORS[status] || "default";
        return <Tag color={color}>{status}</Tag>;
    };

    const prepareData = (newStatus, additionalData = {}) => ({
        ...additionalData,
        academicStrand: editedStrand,
        status: newStatus,
        applicantId: applicant._id,
    });

    const handleStatusUpdate = async (newStatus, additionalData = {}) => {
        setTargetStatus(newStatus);
        setUpdateLoading(true);

        try {
            const data = prepareData(newStatus, additionalData);
            await onStatusUpdate(applicant, newStatus, data);
            await fetchApplicants()
            setUpdateLoading(false)
            onClose()
        } catch (error) {
            notification.error({
                message: "Error",
                description: "Failed to update application status",
            });
        } finally {
            setUpdateLoading(false);
            setTargetStatus(null);
        }
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <EyeOutlined className="mr-2 text-blue-500" />
                    <span className="text-lg font-medium">Applicant Detail Information</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={
                <Space>
                    <Button
                        key="reject"
                        danger
                        icon={updateLoading && targetStatus === "Rejected" ? <LoadingOutlined /> : <CloseCircleOutlined />}
                        onClick={() => handleStatusUpdate("Rejected")}
                        loading={updateLoading && targetStatus === "Rejected"}
                        disabled={updateLoading || applicant?.interviewStatus === "Rejected" || applicant?.examStatus === "Rejected"}
                    >
                        Reject
                    </Button>
               {/*      <Button
                        key="waitinglist"
                        type="default"
                        icon={updateLoading && targetStatus === "On-waiting-list" ? <LoadingOutlined /> : <ClockCircleOutlined />}
                        onClick={() => handleStatusUpdate("On-waiting-list")}
                        loading={updateLoading && targetStatus === "On-waiting-list"}
                        disabled={updateLoading || applicant?.interviewStatus === "On-waiting-list" || applicant?.examStatus === "On-waiting-list"}
                        style={{ backgroundColor: "#faad14", borderColor: "#faad14", color: "white" }}
                    >
                        On Waiting List
                    </Button> */}
                    <Button
                        key="approve"
                        type="primary"
                        icon={updateLoading && targetStatus === "Approved" ? <LoadingOutlined /> : <CheckCircleOutlined />}
                        onClick={() => handleStatusUpdate("Approved")}
                        loading={updateLoading && targetStatus === "Approved"}
                        disabled={updateLoading || applicant?.interviewStatus === "Approved" || applicant?.examStatus === "Approved"}
                        style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                    >
                        Approve
                    </Button>
                    <Button 
                        key="close" 
                        onClick={onClose} 
                        disabled={updateLoading}
                        icon={updateLoading ? <LoadingOutlined /> : null}
                    >
                        Close
                    </Button>
                </Space>
            }
            width={800}
            centered
        >
            <div className="p-2">
                <Card className="mb-4 border-l-4" style={{ borderLeftColor: "#1890ff" }}>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div className="text-center mb-4">
                                <Title level={4} style={{ margin: 0 }} className="mb-1">
                                    {applicant.firstName} {applicant.middleName} {applicant.lastName}
                                </Title>
                                <Tag color="blue" className="text-base px-3 py-1">
                                    {applicant.applicantID}
                                </Tag>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Descriptions title="Personal Information" bordered size="middle" column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }} className="mb-4">
                    <Descriptions.Item label="First Name">{applicant.firstName}</Descriptions.Item>
                    <Descriptions.Item label="Middle Name">{applicant.middleName || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Last Name">{applicant.lastName}</Descriptions.Item>
                    <Descriptions.Item label="Gender">{applicant.gender || "Not specified"}</Descriptions.Item>
                    <Descriptions.Item label="Email" span={2}>{applicant.email}</Descriptions.Item>
                    <Descriptions.Item label="Mobile Number">{applicant.mobile}</Descriptions.Item>
                    <Descriptions.Item label="Address" span={2}>{applicant.address || "No address provided"}</Descriptions.Item>
                </Descriptions>

                <Descriptions title="Academic Information" bordered size="middle" column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }} className="mb-4">
                    <Descriptions.Item label="Academic Level">
                        <Tag color="blue">{applicant.academicLevel}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Strand">
                        <Select
                            value={editedStrand}
                            onChange={setEditedStrand}
                            options={ACADEMIC_STRAND_OPTIONS}
                            style={{ width: 120 }}
                            placeholder="Select strand"
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Year">{applicant.academicYear}</Descriptions.Item>
                    <Descriptions.Item label="Previous School">{applicant.previousSchool || "N/A"}</Descriptions.Item>
                </Descriptions>

                <Descriptions title="Application Status" bordered size="middle" column={{ xxl: 3, xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }} className="mb-4">
                    <Descriptions.Item label="Admission Status">
                        {renderStatusTag(applicant.admissionAdminFirstStatus)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Interview Status">
                        {renderStatusTag(applicant.approvedExamInterviewResult || "Pending")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Exam Details Status">
                        {renderStatusTag(applicant.examInterviewResultStatus || "Pending")}
                    </Descriptions.Item>
                </Descriptions>

                <Descriptions title="Additional Information" bordered size="middle" column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
                    <Descriptions.Item label="Application Date">{formatDate(applicant.createdAt)}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated">{formatDate(applicant.updatedAt)}</Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>{applicant.notes || "No additional notes"}</Descriptions.Item>
                </Descriptions>
            </div>
        </Modal>
    );
};

export default ApplicantDetailsModal;
